import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CallDisposition, CallSession } from './call-types';

/**
 * Service for call-related HTTP API operations
 * Handles backend communication for call records and history
 */

export interface CallRecord {
  id?: number;
  lead_id?: number;
  phone_number: string;
  direction: string;
  start_time: Date | string;
  end_time?: Date | string;
  duration?: number;
  disposition?: CallDisposition;
  notes?: string;
  recording_url?: string;
  telnyx_call_id?: string;
  backend_call_id?: number;
  user_id?: number;
  company_id?: number;
  cost?: number;
  status?: string;
}

export interface DispositionUpdate {
  disposition: CallDisposition;
  notes?: string;
  scheduled_follow_up?: Date;
  outcome?: string;
  tags?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class CallApiService {
  private readonly baseUrl = `${environment.apiUrl}/calls`;

  constructor(private http: HttpClient) {}

  /**
   * Create a new call record
   * @param callData - Call record data
   * @returns Observable with created call record
   */
  createCall(callData: CallRecord): Observable<any> {
    return this.http.post(`${this.baseUrl}`, callData);
  }

  /**
   * Update call disposition
   * @param callId - Backend call ID
   * @param dispositionData - Disposition update data
   * @returns Observable with update result
   */
  updateDisposition(callId: number, dispositionData: DispositionUpdate): Observable<any> {
    return this.http.patch(`${this.baseUrl}/${callId}/disposition`, dispositionData);
  }

  /**
   * Link Telnyx call control ID to call record
   * @param callId - Backend call ID
   * @param telnyxCallControlId - Telnyx call control ID from WebRTC
   * @returns Observable with update result
   */
  linkTelnyxId(callId: number, telnyxCallControlId: string): Observable<any> {
    return this.http.patch(`${this.baseUrl}/${callId}/link-telnyx`, { telnyxCallControlId });
  }

  /**
   * Get call by ID
   * @param callId - Backend call ID
   * @returns Observable with call data
   */
  getCall(callId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/${callId}`);
  }

  /**
   * Get call history for a lead
   * @param leadId - Lead ID
   * @param limit - Maximum number of records to return
   * @returns Observable with call history
   */
  getLeadCallHistory(leadId: number, limit: number = 50): Observable<any> {
    return this.http.get(`${this.baseUrl}/history/lead/${leadId}`, {
      params: { limit: limit.toString() }
    });
  }

  /**
   * Export call history
   * @param filters - Export filters
   * @returns Observable with export blob
   */
  exportCallHistory(filters?: any): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/export`, {
      params: filters || {},
      responseType: 'blob'
    });
  }

  /**
   * Notify backend of call answer (non-blocking)
   * @param callId - Backend call ID
   * @param data - Answer notification data
   * @returns Observable with notification result
   */
  notifyCallAnswer(callId: number, data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/${callId}/answer`, data);
  }

  /**
   * Notify backend of call cancellation
   * @param callId - Backend call ID
   * @param reason - Cancellation reason
   * @returns Observable with notification result
   */
  notifyCallCancellation(callId: number, reason: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/${callId}/cancel`, { reason });
  }

  /**
   * Build call record from session
   * @param session - Call session
   * @param userId - User ID
   * @param companyId - Company ID
   * @returns Call record object
   */
  buildCallRecord(session: CallSession, userId?: number, companyId?: number): CallRecord {
    return {
      lead_id: session.leadId,
      phone_number: session.phoneNumber,
      direction: session.direction,
      start_time: session.startTime,
      end_time: session.endTime,
      duration: session.duration,
      disposition: session.disposition,
      notes: session.notes,
      recording_url: session.recordingUrl,
      telnyx_call_id: session.telnyxCallId,
      backend_call_id: typeof session.backendCallId === 'number' ? session.backendCallId : (session.backendCallId ? Number(session.backendCallId) : undefined),
      user_id: userId,
      company_id: companyId,
      status: session.state === 'ended' ? 'completed' : 'failed'
    };
  }

}
